import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import './About.css'; // Reuse About page styles

const DEGREE_ABBR = {
  'Master of Business Administration': 'MBA',
  'Master of Arts': 'MA',
  'Master of Science': 'MSc',
  'Bachelor of Engineering': 'BE',
  'Bachelor of Science': 'BSc',
  'Bachelor of Arts': 'BA',
  'Doctor of Philosophy': 'PhD',
};

const Education = () => {
  const [education, setEducation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEducation = async () => {
      try {
        const response = await axios.get('/api/education');
        setEducation(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching education data:', err);
        setError('Failed to load education data. Please try again later.');
        setLoading(false);
      }
    };

    fetchEducation();
  }, []);

  if (loading) {
    return (
      <Container className="py-5 mt-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading education information...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5 mt-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </Container>
    );
  }

  return (
    <div className="mt-5 pt-4">
      <section className="section">
        <Container>
          <h2 className="section-title text-center mb-5">Education</h2>
          <div className="section-intro text-center mb-5">
            <p className="lead">
              Academic background and formal education
            </p>
          </div>
          <Row>
            <Col lg={8} className="mx-auto">
              <div className="timeline">
                {education.map((edu, index) => (
                  <div className="education-card mb-4" key={index}>
                    <div className="card-body text-left">
                      <div className="education-header">
                        <div className="education-title-group">
                          <h3 className="education-degree">
                            {DEGREE_ABBR[edu.degree] ? `${DEGREE_ABBR[edu.degree]} (${edu.degree})` : edu.degree}
                          </h3>
                          {edu.field && (
                            <p className="education-field">Specialization in {edu.field}</p>
                          )}
                        </div>
                        <div className="education-duration">
                          {edu.start_date} – {edu.end_date}
                        </div>
                      </div>
                      <div className="education-institution mb-2">
                        <div className="institution-info">
                          {edu.logo && (
                            <img
                              src={edu.logo}
                              alt={`${edu.institution} logo`}
                              className="institution-logo-square"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                          )}
                          <span className="institution-logo-fallback-square" style={{display: edu.logo ? 'none' : 'block'}}>
                            {edu.institution.charAt(0).toUpperCase()}
                          </span>
                          <div className="institution-details">
                            <span className="institution-name">{edu.institution}</span>
                            {edu.location && (
                              <span className="institution-location">{edu.location}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {edu.achievements && edu.achievements.length > 0 && (
                        <div className="education-achievements">
                          <ul className="achievements-list">
                            {edu.achievements.map((achievement, i) => (
                              <li key={i}>{achievement}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="section bg-gradient-dark graduation-photos-section">
        <Container>
          <h2 className="section-title text-center mb-5">Graduation Memories</h2>
          <div className="section-intro text-center mb-5">
            <p className="lead">
              Celebrating the completion of my MBA in Artificial Intelligence from IU Berlin
            </p>
          </div>
          <Row className="justify-content-center">
            <Col lg={12}>
              <div className="graduation-gallery">
                <Row>
                  <Col lg={4} md={6} className="mb-4">
                    <div className="graduation-photo-card">
                      <img
                        src={`${process.env.PUBLIC_URL}/images/education/PANA1043.webp`}
                        alt="Graduation ceremony - IU Berlin"
                        className="graduation-photo"
                        loading="lazy"
                      />
                      <div className="photo-overlay">
                        <div className="photo-caption">
                          <h5>Graduation Day</h5>
                          <p>MBA in Artificial Intelligence</p>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col lg={4} md={6} className="mb-4">
                    <div className="graduation-photo-card">
                      <img
                        src={`${process.env.PUBLIC_URL}/images/education/PANA1457.webp`}
                        alt="Graduation celebration - IU Berlin"
                        className="graduation-photo"
                        loading="lazy"
                      />
                      <div className="photo-overlay">
                        <div className="photo-caption">
                          <h5>Achievement Moment</h5>
                          <p>International University of Applied Sciences</p>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col lg={4} md={6} className="mb-4">
                    <div className="graduation-photo-card">
                      <img
                        src={`${process.env.PUBLIC_URL}/images/education/PANA1470.webp`}
                        alt="Graduation memories - IU Berlin"
                        className="graduation-photo"
                        loading="lazy"
                      />
                      <div className="photo-overlay">
                        <div className="photo-caption">
                          <h5>Proud Moment</h5>
                          <p>Berlin, Germany</p>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Education;
